import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { uniq } from 'ramda';
import { usePosUsersRequest, usePosMonthRequest, useUpdatePosUserRequest, useUpdatePosUsersRequest, useDownloadPosUserTargetExcelRequest } from "../../hooks/useApi";
import { creatTurnoverState, deepMerge, downloadFile, MONTH_MAP, CURRENCY_MAP, currencyToNumber } from '../../utils/helpers';
import debounce from 'lodash.debounce';
import BackIcon from '../../components/Icons/BackIcon';
import OutlinedBtn from '../../components/OutlinedBtn';
import DownloadIcon from '../../components/Icons/DownloadIcon';
import { NumericFormat } from 'react-number-format';
import { FormatMoney, FormatNumberToOneDigit, FormatNumberToTwoDigits } from '../../utils/number-formats'; 
import PlanStatus from '../../components/PlanStatus';
import TextInput from '../../components/TextInput';
import LoadingOverlay from '../../components/LoadingOverlay';
import ForkIcon from '../../components/Icons/ForkIcon';
import RefreshIcon from '../../components/Icons/RefreshIcon';
import './index.css';

const SAKpi = () => {
  
  const posId = localStorage.getItem('posId');
  const posDs = localStorage.getItem('posDs');
  const yearNumber = localStorage.getItem('selectedYear');
  const monthNumber = localStorage.getItem('month');
  const [targetData, setTargetData] = useState(null);
  const [posUserTotals, setPosUserTotals] = useState(null);
  const [ { loading: fetchDataLoading }, executeRequest ] = usePosUsersRequest();
  const [ { loading: updatePosUsersLoading }, updatePosUsers ] = useUpdatePosUsersRequest();
  const [storeData, setStoreData] = useState(null);
  const [ {}, getStoreData ] = usePosMonthRequest();
  const [ { loading }, updatePosUser ] = useUpdatePosUserRequest();
  const [ { loading: loadingFile}, downloadExcel ] = useDownloadPosUserTargetExcelRequest();
  const [formData, setFormData] = useState({});
  const [downloadError, setDownloadError] = useState(null);
  const posIdsToChange = useRef([]);

  const downloadExcelHandleClick = async (event) => {
    const bodyParams = {
      posId,
      month: monthNumber,
      year: yearNumber
    };
    const fileName = `SaleAssistant- ${posId}, ${yearNumber}-${monthNumber}.xlsx`;
    const error = await downloadFile(bodyParams, fileName, downloadExcel);
    if (error)
      setDownloadError(error);
    // await FetchPosUsers();
  }

  const handleChange = async(event) => {
    const { target: { value, name } } = event;
    const fieldName = event.target.getAttribute('data-prop');

    const newObj = {
      [name]: {
        [fieldName]: currencyToNumber(value)
      }
    };
    const mergedObj = deepMerge(formData, newObj);
    setFormData(mergedObj);
    debouncedSave.cancel();
    const newElement = `${posId}-${name}`;
    posIdsToChange.current.push(newElement);
    debouncedSave(event, mergedObj, posIdsToChange);
  };

  const debouncedSave = useCallback(
    debounce(async(event, mergedObj) => {
      const { target: { value, name } } = event;
      const posIds = uniq(posIdsToChange.current);
      var promises = posIds.map(async item => {
        const posId = item.split('-')[0];
        const userId = item.split('-')[1];
        const bodyParams = {
          posId,
          month: monthNumber,
          year: yearNumber,
          userId,
          averagePrice: mergedObj[userId]['averagePrice'],
          upt: mergedObj[userId]['upt'],
          receiptNumber: mergedObj[userId]['numberOfReceipt'],
          workingDays: mergedObj[userId]['workingDays'],
          target: mergedObj[userId]['planned'],
        };

        await updatePosUser({ bodyParams });
      });
      Promise.all(promises).then(async()=> {
        posIdsToChange.current = [];
        await FetchPosUsers();
      });
      
    }, 1500),
    [], // will be created only once initially
  );

  const FetchPosUsers = useCallback(async () => {
    if (posId && monthNumber && yearNumber)
    {
      const bodyParams = {
        posId,
        month: monthNumber,
        year: yearNumber
      };

      const response = await executeRequest({ bodyParams });
      setTargetData(response?.data?.posUserTargets);
      setPosUserTotals(response?.data?.total);
      setFormData(creatTurnoverState(response?.data?.posUserTargets));

      const storeResponse = await getStoreData({ bodyParams });
      setStoreData(storeResponse?.data);
    }
  }, [posId, monthNumber, yearNumber])

  useEffect(() => {
    FetchPosUsers();
  }, [posId, monthNumber, yearNumber]);

  const handleSplitBudget = useCallback(async () => {
    const userIds = targetData.map(( { userId } ) => { return userId });
    const monthValue = userIds.length > 0 ? Math.floor(budget / userIds.length) : 0;
    const bodyParams = { posId, userIds, month: monthNumber, year: yearNumber, target: monthValue };
    await updatePosUsers({ bodyParams });
    await FetchPosUsers();
    return false;
  }, [targetData]);

  const handleResetBudget = useCallback(async () => {
    const userIds = targetData.map(( { userId } ) => { return userId });
    const bodyParams = { posId, userIds, month: monthNumber, year: yearNumber, target: 0 };
    await updatePosUsers({ bodyParams });
    await FetchPosUsers();
    return false;
  }, [targetData]);


  if ((targetData == null) || loadingFile || updatePosUsersLoading)
    return <LoadingOverlay />;

  const budget = storeData?.budget || 0;
  const plannedBudget = storeData?.planned || 0;
  const remaining = plannedBudget - budget;
  const splitIsEnabled = targetData[0]?.isEditable;
  const currency = CURRENCY_MAP[storeData?.currency] ? CURRENCY_MAP[storeData?.currency] : storeData?.currency;

  return (
    <div className="saKpiPage">
      <div className="etroContainer titleWrapper dFlex">
        <div className="dFlex justifyContentStart alignItemsCenter gap-1 left">
          <Link to={`/`}><BackIcon /></Link>
          <h1>{posDs} Turnover / {MONTH_MAP[monthNumber]} {yearNumber}</h1>
        </div>
        <div className="center">Client Advisor KPI</div>
        <div className="right dFlex justifyContentEnd alignItemsCenter gap-2">
          <OutlinedBtn title="Download EXCEL" variant="outlined" onClick={downloadExcelHandleClick} startIcon={<DownloadIcon />} color={downloadError ? 'error' : ''} />
          {downloadError && (<span className="textRed mutedText">{downloadError}</span>)}
        </div>
      </div>
      
      
      <div className="tableWrapper">
        <div className="budgetWrapper gap-3">
          <div className="budgetItem">
            <div className="title">Budget:</div>
            <div className="value">{<FormatMoney value={budget} prefix={currency} />}</div> 
          </div>
          <div className="budgetItem dFlex justifyContentSpaceBetween">
            <div>
              <div className="title">Planned:</div> 
              <div className="value">{<FormatMoney value={plannedBudget} prefix={currency} />}</div> 
            </div>
            <PlanStatus value={remaining} />
          </div>
          <div className="budgetItem">
            <div className="title">Remaining:</div> 
            <div className="value">{<NumericFormat className={remaining < 0 ? 'underTarget' : ''} value={remaining} thousandSeparator=" " prefix={`${currency} `} displayType="text" decimalScale={0} />}</div> 
          </div>
        </div>

        <div className="budgetWrapper gap-3">
          <div className="budgetItem">
            <div className="title">UPT:</div>
            <div className="value">{<NumericFormat value={storeData?.upt} thousandSeparator=" " displayType="text" decimalScale={2} />}</div> 
          </div>
          <div className="budgetItem">
            <div className="title">Average price:</div>
            <div className="value">{<FormatMoney value={storeData?.averagePrice} prefix={currency} decimalScale={1} />}</div> 
          </div>
          <div className="budgetItem">
            <div className="title"># Transaction:</div> 
            <div className="value">{<NumericFormat value={storeData?.numberOfReceipt} thousandSeparator=" " displayType="text" decimalScale={0} />}</div> 
          </div>
          <div className="budgetItem">
            <div className="title">ATV:</div>
            <div className="value">{<NumericFormat value={storeData?.vpt} thousandSeparator=" " displayType="text" decimalScale={2} />}</div> 
          </div>
        </div>
        { targetData.length > 0 && (
          <div className="sa-kpi-table-wrapper">
            <table className="monthlySalesTableKpi saKpiTable" style={{ marginTop: '32px' }}>
              <thead>
                <tr className="bgTableGrey">
                  <th rowSpan="2" width="150" className="textLeft">Client Advisor</th>
                  <th colSpan="3">
                    <div style={{display: "flex", "justifyContent": "space-between"}}>
                      <div>Target</div>
                      {/* <div className="tabWrapper">
                        <a disabled={true} className={splitIsEnabled ? "tabLink splitBudget" : "tabLink"} onClick={splitIsEnabled ? handleSplitBudget : null} ><ForkIcon /> Split Budget</a>
                        &nbsp;&nbsp;&nbsp;
                        <a className="tabLink resetAll" onClick={splitIsEnabled ? handleResetBudget : null} ><RefreshIcon /> Reset All</a>
                      </div> */}
                    </div>
                  </th>
                  <th colSpan="3">Daily Target</th>
                  <th colSpan="3">Average Price</th>
                  <th colSpan="3">UPT</th>
                  <th colSpan="3">ATV</th>
                  <th colSpan="3"># Transactions</th>
                  <th rowSpan="2">Working Days</th>
                </tr>
                <tr className="bgTableGrey textCenter splitCols">
                  <th className="borderRightNone pl0 pr0 textCenter targetPlanned">Planned</th>
                  <th className="borderRightNone borderLeftNone pl0 pr0 textCenter">Actual</th>
                  <th className="borderLeftNone pl0 pr0 textCenter">Ratio</th>
                  
                  <th className="borderRightNone pl0 pr0 textCenter">Planned</th>
                  <th className="borderRightNone borderLeftNone pl0 pr0 textCenter">Actual</th>
                  <th className="borderLeftNone pl0 pr0 textCenter">Ratio</th>
                  
                  <th className="borderRightNone pl0 pr0 textCenter">Planned</th>
                  <th className="borderRightNone borderLeftNone pl0 pr0 textCenter">Actual</th>
                  <th className="borderLeftNone pl0 pr0 textCenter">Ratio</th>
                  
                  <th className="borderRightNone pl0 pr0 textCenter">Planned</th>
                  <th className="borderRightNone borderLeftNone pl0 pr0 textCenter">Actual</th>
                  <th className="borderLeftNone pl0 pr0 textCenter">Ratio</th>

                  <th className="borderRightNone pl0 pr0 textCenter">Planned</th>
                  <th className="borderRightNone borderLeftNone pl0 pr0 textCenter">Actual</th>
                  <th className="borderLeftNone pl0 pr0 textCenter">Ratio</th>
                  
                  <th className="borderRightNone pl0 pr0 textCenter">Planned</th>
                  <th className="borderRightNone borderLeftNone pl0 pr0 textCenter">Actual</th>
                  <th className="borderLeftNone pl0 pr0 textCenter">Ratio</th>
                </tr>
              </thead>
              <tbody>
                {
                  targetData.map(({userId, firstName, lastName, target, averageDailyTarget, averagePrice, upt, vpt, numberOfReceipt, workingDays, isEditable}) => {
                    return (
                      <tr className="" key={userId}>
                        <td className="textLeft leftPadding textUppercase">{firstName} {lastName}</td>
                        
                        <td style={{paddingLeft: '10px'}} className={isEditable ? "borderRightNone leftPadding targetPlanned textCenter" : "borderRightNone targetPlanned textCenter" }>
                          {isEditable && (
                            <FormatMoney 
                              value={formData[userId]['planned']}
                              prefix={currency}
                              // renderText={(value) =>
                              //   <TextInput
                              //     value={value}
                              //     name={userId}
                              //     onChange={handleChange}
                              //     dataProp={"planned"}
                              //   />
                              // }
                            />
                          )}
                          {!isEditable && (<NumericFormat value={target.planned || 0} prefix={`${CURRENCY_MAP[storeData?.currency]} `} thousandSeparator=" " displayType="text" decimalScale={0} />)}
                        </td>
                        <td className="borderRightNone borderLeftNone textCenter actual">{<NumericFormat value={target.actual || 0} prefix={`${CURRENCY_MAP[storeData?.currency]} `} thousandSeparator=" " displayType="text" decimalScale={0} />}</td>
                        <td className={target?.ratio >= 100 ? 'borderLeftNone textCenter ratio green' : 'borderLeftNone textCenter ratio'}><NumericFormat value={target.ratio || 0} thousandSeparator=" " displayType="text" decimalScale={2} />%</td>

                        <td className="borderRightNone textCenter planned">{<NumericFormat value={averageDailyTarget.planned || 0} prefix={`${CURRENCY_MAP[storeData?.currency]} `} thousandSeparator=" " displayType="text" decimalScale={2} />}</td>
                        <td className="borderRightNone borderLeftNone textCenter actual">{<NumericFormat value={averageDailyTarget.actual || 0} prefix={`${CURRENCY_MAP[storeData?.currency]} `} thousandSeparator=" " displayType="text" decimalScale={2} />}</td>
                        <td className={averageDailyTarget?.ratio >= 100 ? 'borderLeftNone textCenter ratio green' : 'borderLeftNone textCenter ratio'}><NumericFormat value={averageDailyTarget.ratio || 0} thousandSeparator=" " displayType="text" decimalScale={2} />%</td>
                        
                        <td className={isEditable ? "borderRightNone leftPadding threeDigitAndAndorement" : "borderRightNone threeDigitAndAndorement" }>
                          {isEditable && (
                            <FormatMoney 
                              value={formData[userId]['averagePrice']}
                              prefix={currency}
                              decimalScale={2}
                              renderText={(value) =>
                                <TextInput
                                  name={userId}
                                  onChange={handleChange}
                                  dataProp={"averagePrice"}
                                  value={value}
                                />
                              }
                            />
                          )}
                          {!isEditable && (averagePrice.planned)}
                        </td>
                        <td className="borderRightNone borderLeftNone textCenter actual"><NumericFormat value={averagePrice.actual || 0} prefix={`${CURRENCY_MAP[storeData?.currency]} `} thousandSeparator=" " displayType="text" decimalScale={2} /></td>
                        <td className={averagePrice?.ratio >= 100 ? 'borderLeftNone textCenter ratio green' : 'borderLeftNone textCenter ratio'}><NumericFormat value={averagePrice.ratio || 0} thousandSeparator=" " displayType="text" decimalScale={2} />%</td>
                        
                        <td className={isEditable ? "borderRightNone leftPadding threeDigit" : "borderRightNone threeDigit" }>
                          {isEditable && (
                            <FormatMoney 
                              value={formData[userId]['upt']}
                              prefix={""}
                              decimalScale={2}
                              renderText={(value) =>
                                <TextInput
                                  value={value}
                                  name={userId}
                                  onChange={handleChange}
                                  dataProp={"upt"}
                                />
                              }
                            />
                          )}
                          {!isEditable && (upt.planned)}
                        </td>
                        <td className="borderRightNone borderLeftNone textCenter actual"><NumericFormat value={upt.actual || 0} thousandSeparator=" " displayType="text" decimalScale={2} /></td>
                        <td className={upt?.ratio >= 100 ? 'borderLeftNone textCenter ratio green' : 'borderLeftNone textCenter ratio'}><NumericFormat value={upt.ratio || 0} thousandSeparator=" " displayType="text" decimalScale={2} />%</td>
                        
                        <td className="borderRightNone textCenter planned"><NumericFormat value={vpt.planned || 0} thousandSeparator=" " displayType="text" decimalScale={2} /></td>
                        <td className="borderRightNone borderLeftNone textCenter actual"><NumericFormat value={vpt.actual || 0} thousandSeparator=" " displayType="text" decimalScale={2} /></td>
                        <td className={vpt?.ratio >= 100 ? 'borderLeftNone textCenter ratio green' : 'borderLeftNone textCenter ratio'}><NumericFormat value={vpt.ratio || 0} thousandSeparator=" " displayType="text" decimalScale={2} />%</td>
                        
                        <td className={isEditable ? "borderRightNone leftPadding threeDigit" : "borderRightNone threeDigit" }>
                          {isEditable && (
                            <FormatMoney 
                              value={formData[userId]['numberOfReceipt']}
                              prefix={""}
                              renderText={(value) =>
                                <TextInput
                                  value={value}
                                  name={userId}
                                  onChange={handleChange}
                                  dataProp={"numberOfReceipt"}
                                />
                              }
                            />
                          )}
                          {!isEditable && (numberOfReceipt.planned)}
                        </td>
                        <td className="borderRightNone borderLeftNone textCenter actual"><NumericFormat value={numberOfReceipt.actual || 0} thousandSeparator=" " displayType="text" decimalScale={0} /></td>
                        <td className={numberOfReceipt?.ratio >= 100 ? 'borderLeftNone textCenter ratio green' : 'borderLeftNone textCenter ratio'}><NumericFormat value={numberOfReceipt.ratio || 0} thousandSeparator=" " displayType="text" decimalScale={2} />%</td>
                        
                        <td className={isEditable ? "leftPadding threeDigit" : "threeDigit" }>
                          {isEditable && (
                            <TextInput
                              defaultValue={formData[userId]['workingDays']}
                              name={userId}
                              onChange={handleChange}
                              dataProp={"workingDays"}
                            />
                          )}
                          {!isEditable && (workingDays)}
                        </td>
                      </tr>
                    );
                  })
                }
                <tr className="bgTableGrey textCenter">
                  <td className="textLeft">Total</td>

                  <td><FormatMoney value={posUserTotals?.target?.planned} /></td>
                  <td><FormatMoney value={posUserTotals?.target?.actual} /></td>
                  <td className={posUserTotals?.target?.ratio >= 100 ? 'ratio green' : 'ratio'}><FormatNumberToTwoDigits value={posUserTotals?.target?.ratio} />%</td>

                  <td><FormatMoney decimalScale={2} value={posUserTotals?.averageDailyTarget?.planned} /></td>
                  <td><FormatMoney decimalScale={2} value={posUserTotals?.averageDailyTarget?.actual} /></td>
                  <td className={posUserTotals?.averageDailyTarget?.ratio >= 100 ? 'ratio green' : 'ratio'}><FormatNumberToTwoDigits value={posUserTotals?.averageDailyTarget?.ratio} />%</td>

                  <td><FormatMoney decimalScale={2} value={posUserTotals?.averagePrice?.planned} /></td>
                  <td><FormatMoney decimalScale={2} value={posUserTotals?.averagePrice?.actual} /></td>
                  <td className={posUserTotals?.averagePrice?.ratio >= 100 ? 'ratio green' : 'ratio'}><FormatNumberToTwoDigits value={posUserTotals?.averagePrice?.ratio} />%</td>
                  
                  <td><FormatNumberToTwoDigits value={posUserTotals?.upt?.planned} /></td>
                  <td><FormatNumberToTwoDigits value={posUserTotals?.upt?.actual} /></td>
                  <td className={posUserTotals?.upt?.ratio >= 100 ? 'ratio green' : 'ratio'}><FormatNumberToTwoDigits value={posUserTotals?.upt?.ratio} />%</td>
                  
                  <td><FormatNumberToTwoDigits value={posUserTotals?.vpt?.planned} /></td>
                  <td><FormatNumberToTwoDigits value={posUserTotals?.vpt?.actual} /></td>
                  <td className={posUserTotals?.vpt?.ratio >= 100 ? 'ratio green' : 'ratio'}><FormatNumberToTwoDigits value={posUserTotals?.vpt?.ratio} />%</td>

                  <td>{posUserTotals?.numberOfReceipt?.planned}</td>
                  <td>{posUserTotals?.numberOfReceipt?.actual}</td>
                  <td className={posUserTotals?.numberOfReceipt?.ratio >= 100 ? 'ratio green' : 'ratio'}><FormatNumberToTwoDigits value={posUserTotals?.numberOfReceipt?.ratio} />%</td>

                  <td>{posUserTotals?.workingDays}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
        { targetData.length <= 0 && (
          <h2 className="mt-4 text-center fs-32 font-bold">No Client Advisors found</h2>
        )}
        <br/><br/><br/>
      </div>
    </div>  
  );
};

export default SAKpi;
